import { useCallback, useMemo, useRef, useState } from 'react'

import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useDebounceCallback } from 'usehooks-ts'

import {
  CreatePhysicalLocationInput,
  PhysicalLocation,
  PlaceDetails,
} from '@tribeplatform/gql-client/types'
import {
  useAutocompletePhysicalLocation,
  useGetPlaceDetails,
} from '@tribeplatform/react-sdk/hooks'
import { FormControlSearchableSelect } from '@tribeplatform/react-ui-kit/FormControl/FormControlSearchableSelect'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { Form } from '../../Form/index.js'
import { T, useI18n } from '../../i18n/index.js'

import { LocationMap } from './LocationMap.js'
import { VirtualLocationsPicker } from './VirtualLocationsPicker.js'

export const EventLocationPicker = () => {
  const { control } = useFormContext()

  return (
    <div className="flex flex-col gap-3">
      <Controller
        control={control}
        name="physicalLocations"
        render={({ field }) => {
          return (
            <PhysicalLocationsPicker
              location={field.value?.at(0)}
              onLocationChange={location => {
                if (!location) {
                  field.onChange([])
                  return
                }
                field.onChange([location])
              }}
            />
          )
        }}
      />
      <AdditionalInfoField />
      <VirtualLocationsPicker />
    </div>
  )
}

interface LocationPickerProps {
  location?: PhysicalLocation | CreatePhysicalLocationInput
  onLocationChange: (location?: CreatePhysicalLocationInput) => void
}

const PhysicalLocationsPicker = ({
  location,
  onLocationChange,
}: LocationPickerProps) => {
  const onPlaceSelect = useCallback(
    (place?: PlaceDetails | null) => {
      if (!place) {
        onLocationChange(null)
        return
      }
      onLocationChange({
        displayName: place.displayName,
        formattedAddress: place.formattedAddress,
        latitude: place.latitude,
        longitude: place.longitude,
        placeId: place.placeId,
        plusCode: place.plusCode,
      })
    },
    [onLocationChange],
  )

  return (
    <div className="flex flex-col gap-2">
      <PlaceAutocomplete
        onPlaceSelect={onPlaceSelect}
        value={
          location
            ? {
                mainText: location.displayName ?? '',
                secondaryText: location.formattedAddress ?? '',
                placeId: location.placeId ?? '',
              }
            : undefined
        }
      />
      {location && (
        <LocationMap
          latitude={location.latitude}
          longitude={location.longitude}
          placeId={location.placeId}
        />
      )}
    </div>
  )
}

interface PlaceAutocompleteProps {
  onPlaceSelect: (place?: PlaceDetails | null) => void
  value?: {
    mainText: string
    secondaryText?: string
    placeId?: string
  }
}

const PlaceAutocomplete = ({
  onPlaceSelect,
  value,
}: PlaceAutocompleteProps) => {
  const { $t } = useI18n()
  const [debouncedInput, setDebouncedInput] = useState('')
  const sessionToken = useRef<string | null>(null)
  const { getPlaceDetails } = useGetPlaceDetails()
  const { data, isInitialLoading: isLoading } = useAutocompletePhysicalLocation(
    {
      variables: {
        input: {
          input: debouncedInput,
          sessionToken: sessionToken.current ?? undefined,
        },
      },
      fields: {
        suggestions: 'all',
      },
      useQueryOptions: {
        enabled: !!debouncedInput,
        onSuccess: data => {
          sessionToken.current = data.sessionToken
        },
      },
    },
  )

  const predictionResults = useMemo(() => data?.suggestions || [], [data])

  const debouncedGetPlacePredictions = useDebounceCallback(
    setDebouncedInput,
    500,
  )

  const options = useMemo(() => {
    const items = predictionResults.map(prediction => ({
      value: prediction.placeId,
      key: prediction.placeId,
      text: (
        <SuggestedPlaceItem
          mainText={prediction.mainText}
          secondaryText={prediction.secondaryText}
        />
      ),
    }))

    if (items.length === 0 && value) {
      return [
        {
          value: value.placeId,
          key: value.placeId,
          text: (
            <SuggestedPlaceItem
              mainText={value.mainText}
              secondaryText={value.secondaryText}
            />
          ),
        },
      ]
    }

    return items
  }, [predictionResults, value])

  const onSelect = useCallback(
    async (placeId?: string) => {
      if (!placeId) {
        onPlaceSelect(null)
        return
      }
      try {
        const details = await getPlaceDetails(placeId, sessionToken.current)
        sessionToken.current = null
        onPlaceSelect(details)
      } catch {
        toast({
          title: $t({
            defaultMessage: 'Something went wrong',
            id: 'Generics.SomethingWentWrong',
          }),
          description: $t({
            defaultMessage: 'Failed to get place details, please try again.',
            id: 'Event.Form.PhysicalLocation.FailedToGetDetails.Description',
          }),
          status: 'error',
        })
      }
    },
    [$t, getPlaceDetails, onPlaceSelect],
  )

  return (
    <FormControlSearchableSelect
      name="physicalLocations"
      label={<T defaultMessage="Event location" id="Event.Form.Location" />}
      options={options}
      onInputChange={debouncedGetPlacePredictions}
      onChange={onSelect}
      value={value?.placeId}
      placeholder={$t({
        defaultMessage: 'Search for a location',
        id: 'Event.Form.PhysicalLocation.Search',
      })}
      hideEmptyOptions={!debouncedInput || isLoading}
    />
  )
}

interface SuggestedPlaceItemProps {
  mainText: string
  secondaryText?: string
}

const SuggestedPlaceItem = ({
  mainText,
  secondaryText,
}: SuggestedPlaceItemProps) => {
  const text = secondaryText ? `${mainText} - ${secondaryText}` : mainText

  return (
    <div className="flex gap-x-2 items-center">
      <SvgIcon name="location-marker" className="size-6 text-content-muted" />
      <div>{text}</div>
    </div>
  )
}

const AdditionalInfoField = () => {
  const { $t } = useI18n()
  const { control } = useFormContext()
  const location = useWatch({
    control,
    name: 'physicalLocations',
  })

  if (!location?.at(0)) {
    return null
  }

  return (
    <Form.Textarea
      name="physicalLocations.0.additionalData"
      placeholder={$t({
        defaultMessage:
          'Add any details, requirements, or next steps for attendees.',
        id: 'Event.Form.Location.AdditionalInfo.Placeholder',
      })}
      label={
        <T
          id="Event.Form.Location.AdditionalInfo"
          defaultMessage="Additional location Info"
        />
      }
    />
  )
}
